/* ═══════════════════════════════════════════════════════════
   wholesale-progress.js
   Custom Element <wholesale-progress> (ficha de producto)
   El estado inicial llega renderizado desde Liquid; este componente
   lo mantiene al día cuando cambia el carrito o la variante.
   La barra se guía solo por la cantidad de la variante en el carrito.
   ═══════════════════════════════════════════════════════════ */

if (!customElements.get('wholesale-progress')) {
  // [thousands separator, decimal separator, decimals] per Shopify money placeholder
  const MONEY_FORMATS = {
    amount: [',', '.', 2],
    amount_no_decimals: [',', '.', 0],
    amount_with_comma_separator: ['.', ',', 2],
    amount_no_decimals_with_comma_separator: ['.', ',', 0],
    amount_with_apostrophe_separator: ["'", '.', 2],
    amount_no_decimals_with_space_separator: [' ', ',', 0],
    amount_with_space_separator: [' ', ',', 2],
    amount_with_period_and_space_separator: [' ', '.', 2]
  };

  customElements.define(
    'wholesale-progress',
    class WholesaleProgress extends HTMLElement {
      constructor() {
        super();
        this.unsubscribers = [];
        this.cartRequest = null;
        this.lastRenderKey = '';
      }

      connectedCallback() {
        const configEl = this.querySelector('script[data-wp-config]');
        if (!configEl) return;
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('wholesale-progress: invalid config', e);
          return;
        }

        this.tiers = (this.config.tiers || [])
          .map((tier) => ({ min: Number(tier.min), pct: Number(tier.pct) }))
          .filter((tier) => tier.min > 0 && tier.pct > 0)
          .sort((a, b) => a.min - b.min);
        if (!this.tiers.length) return;

        this.strings = this.config.strings || {};
        this.maxMin = Math.max(this.tiers[this.tiers.length - 1].min, 1);
        this.sectionId = this.dataset.sectionId;
        this.productId = Number(this.dataset.productId);
        this.variantId = Number(this.config.variantId);
        this.price = Number(this.config.price) || 0;
        this.cartQuantities = this.buildCartQuantities(
          (this.config.cartLines || []).map(([variant_id, quantity]) => ({ variant_id, quantity }))
        );
        this.currentPct = null; // unknown until first render: no announcement on load

        this.els = {
          message: this.querySelector('.wp-text__message'),
          unitPrice: this.querySelector('.wp-text__unit-price'),
          cartNote: this.querySelector('.wp-cart-note'),
          track: this.querySelector('.wp-track'),
          fill: this.querySelector('.wp-track__fill'),
          dots: Array.from(this.querySelectorAll('.wp-track__dot')),
          live: this.querySelector('.wp-live')
        };

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers.push(
            subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event)),
            subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event))
          );
        }

        this.render();
      }

      disconnectedCallback() {
        this.unsubscribers.forEach((unsubscribe) => unsubscribe());
        this.unsubscribers = [];
        if (this.cartRequest) this.cartRequest.abort();
      }

      /* ── Events ──────────────────────────────────────────── */

      onVariantChange(event) {
        if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
        const variant = event.data.variant;
        if (!variant) return;
        this.variantId = Number(variant.id);
        this.price = Number(variant.price) || this.price;
        this.render();
      }

      onCartUpdate(event) {
        if (!event) return;
        if (event.cartData && Array.isArray(event.cartData.items)) {
          this.cartQuantities = this.buildCartQuantities(event.cartData.items);
          this.render();
          return;
        }
        if (event.source !== 'product-form') return;

        // Only the latest request matters when adding to cart several times in a row
        if (this.cartRequest) this.cartRequest.abort();
        const controller = new AbortController();
        this.cartRequest = controller;

        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, signal: controller.signal })
          .then((response) => response.json())
          .then((cart) => {
            this.cartQuantities = this.buildCartQuantities(cart.items || []);
            this.render();
          })
          .catch((e) => {
            if (e.name !== 'AbortError') console.error('wholesale-progress: cart fetch failed', e);
          })
          .finally(() => {
            if (this.cartRequest === controller) this.cartRequest = null;
          });
      }

      buildCartQuantities(items) {
        const quantities = new Map();
        items.forEach((item) => {
          if (item.product_id !== undefined && Number(item.product_id) !== this.productId) return;
          const id = Number(item.variant_id);
          quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity || 0));
        });
        return quantities;
      }

      /* ── Render ──────────────────────────────────────────── */

      render() {
        const qty = this.cartQuantities.get(this.variantId) || 0;

        // Skip DOM work if nothing changed
        const renderKey = `${qty}|${this.price}`;
        if (renderKey === this.lastRenderKey) return;
        this.lastRenderKey = renderKey;

        let current = null;
        let next = null;
        for (const tier of this.tiers) {
          if (qty >= tier.min) current = tier;
          else if (!next) next = tier;
        }
        const currentPct = current ? current.pct : 0;
        const { message, unitPrice, cartNote, track, fill, dots } = this.els;

        let text;
        if (!next) {
          text = this.interpolate(this.strings.maxReached, { pct: currentPct });
        } else if (current) {
          text = this.interpolate(this.strings.addNext, { quantity: next.min - qty, pct: next.pct, current: currentPct });
        } else {
          text = this.interpolate(this.strings.addFirst, { quantity: next.min - qty, pct: next.pct });
        }
        if (message && message.textContent !== text) message.textContent = text;

        if (unitPrice) {
          unitPrice.hidden = currentPct === 0;
          if (currentPct > 0) {
            const unit = Math.round((this.price * (100 - currentPct)) / 100);
            unitPrice.textContent = this.interpolate(this.strings.unitPrice, { price: this.formatMoney(unit) });
          }
        }

        if (cartNote) {
          cartNote.hidden = qty === 0;
          if (qty > 0) cartNote.textContent = this.interpolate(this.strings.inCart, { quantity: qty });
        }

        if (track) {
          track.setAttribute('aria-valuenow', Math.min(qty, this.maxMin));
          track.setAttribute('aria-valuetext', text);
        }
        if (fill) fill.style.width = `${Math.min(qty / this.maxMin, 1) * 100}%`;
        dots.forEach((dot) => dot.classList.toggle('wp-track__dot--reached', qty >= Number(dot.dataset.min)));

        // Announce only when a better tier is reached after the first render
        if (this.currentPct !== null && currentPct > this.currentPct) {
          this.announce(this.interpolate(this.strings.tierReached, { pct: currentPct }));
        }
        this.currentPct = currentPct;
      }

      /* ── Helpers ─────────────────────────────────────────── */

      announce(text) {
        const { live } = this.els;
        if (!live || !text) return;
        live.textContent = '';
        requestAnimationFrame(() => {
          live.textContent = text;
        });
      }

      interpolate(template, values) {
        return String(template || '').replace(/\[(\w+)\]/g, (match, key) =>
          values[key] !== undefined ? String(values[key]) : match
        );
      }

      formatMoney(cents) {
        const format = String(this.config.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholder);
        if (!match) return (cents / 100).toFixed(2);

        const [thousands, decimal, decimals] = MONEY_FORMATS[match[1]] || MONEY_FORMATS.amount;
        const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
        const value = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
        return format.replace(placeholder, value);
      }
    }
  );
}
